import { courses as staticCourses, getCourse as getStaticCourse } from "@/lib/data";
import type { Course, CourseBatch, CourseModule, EnquiryFormKey } from "@/lib/types";

import { API_URL } from "@/lib/apiUrl";

interface ApiCourse {
  id: number;
  title: string;
  slug: string;
  tagline: string | null;
  category_id: number | null;
  category_name: string | null;
  level: string | null;
  duration: string | null;
  mode: string | null;
  summary: string | null;
  description: string[] | null;
  image: string | null;
  accent: string | null;
  fee: number | null;
  gstNote: string | null;
  showFee: boolean;
  weekday_batch?: boolean;
  weekend_batch?: boolean;
  featured: boolean;
  rating: number | null;
  enrolled: number | null;
  instructorSlug: string | null;
  outcomes: string[] | null;
  learn: string[] | null;
  learnIntro: string | null;
  modules: CourseModule[] | null;
  projects: string[] | null;
  certification: string | null;
  certifications: { title: string; desc: string }[] | null;
  batches: CourseBatch[] | null;
  faqs: { q: string; a: string }[] | null;
  whyTitle: string | null;
  why: string[] | null;
  coreSkills: string | null;
  eligibility: string | null;
  prerequisites: string | null;
  batchNote: string | null;
  stats: { value: string; label: string }[] | null;
  careerAreas: { title: string; desc: string }[] | null;
  careersIntro: string | null;
  careersNote: string | null;
  careers: string[] | null;
  enquiryForm: string | null;
  gallery: string[] | null;
}

const mapCourse = (c: ApiCourse): Course => ({
  id: c.id,
  slug: c.slug,
  title: c.title,
  tagline: c.tagline ?? "",
  category: c.category_name ?? "General",
  level: c.level ?? "All Levels",
  duration: c.duration ?? "",
  mode: c.mode ?? "Online",
  summary: c.summary ?? "",
  description: c.description ?? [],
  image: c.image ?? "burgundy",
  accent: c.accent ?? c.image ?? "burgundy",
  fee: c.fee ?? undefined,
  gstNote: c.gstNote ?? undefined,
  showFee: !!c.showFee,
  weekdayBatch: c.weekday_batch ?? false,
  weekendBatch: c.weekend_batch ?? false,
  featured: !!c.featured,
  rating: c.rating ?? undefined,
  enrolled: c.enrolled ?? undefined,
  instructorSlug: c.instructorSlug ?? undefined,
  outcomes: c.outcomes ?? [],
  learn: c.learn ?? [],
  learnIntro: c.learnIntro ?? undefined,
  modules: c.modules ?? [],
  projects: c.projects ?? undefined,
  certification: c.certification ?? "",
  certifications: c.certifications ?? undefined,
  batches: c.batches ?? [],
  faqs: c.faqs ?? undefined,
  whyTitle: c.whyTitle ?? undefined,
  why: c.why ?? undefined,
  coreSkills: c.coreSkills ?? undefined,
  eligibility: c.eligibility ?? undefined,
  prerequisites: c.prerequisites ?? undefined,
  batchNote: c.batchNote ?? undefined,
  stats: c.stats ?? undefined,
  careerAreas: c.careerAreas ?? undefined,
  careersIntro: c.careersIntro ?? undefined,
  careersNote: c.careersNote ?? undefined,
  careers: c.careers ?? undefined,
  enquiryForm: (c.enquiryForm as EnquiryFormKey | null) ?? undefined,
  gallery: c.gallery?.length ? c.gallery : undefined,
});

// Dev: always fresh so dashboard edits show immediately; prod: 60s ISR cache.
const FETCH_OPTS: RequestInit =
  { cache: "no-store" };

export async function getSiteCourses(): Promise<Course[]> {
  try {
    const res = await fetch(`${API_URL}/courses/all`, FETCH_OPTS);
    if (!res.ok) throw new Error(`Courses API responded ${res.status}`);
    const json = (await res.json()) as { data?: ApiCourse[] };
    return (json.data ?? []).map(mapCourse);
  } catch {
    return staticCourses;
  }
}

/** Enabled category names in the dashboard's order (empty if the API is down). */
export async function getCategoryOrder(): Promise<string[]> {
  return (await getCategoryLists()).order;
}

/** Enabled categories in the courses order, plus those flagged for the footer in the footer's own order. */
export async function getCategoryLists(): Promise<{ order: string[]; footer: string[] }> {
  try {
    const res = await fetch(`${API_URL}/courses/categories`, FETCH_OPTS);
    if (!res.ok) return { order: [], footer: [] };
    const json = (await res.json()) as { data?: string[]; footer?: string[] };
    return {
      order: Array.isArray(json.data) ? json.data : [],
      footer: Array.isArray(json.footer) ? json.footer : [],
    };
  } catch {
    return { order: [], footer: [] };
  }
}

export interface CourseQuery {
  search?: string;
  category?: string;
  level?: string;
  mode?: string;
  page?: number;
}

export interface CoursePage {
  courses: Course[];
  categories: string[];
  modes: string[];
  total: number;
  page: number;
  pageCount: number;
  pageSize: number;
}

export const COURSES_PAGE_SIZE = 9;

/** One filtered page of courses - search, filters and paging are all done by the API. */
export async function getCoursePage(q: CourseQuery): Promise<CoursePage> {
  const page = Math.max(1, q.page || 1);
  try {
    const qs = new URLSearchParams({ page: String(page), page_size: String(COURSES_PAGE_SIZE) });
    for (const k of ["search", "category", "level", "mode"] as const) if (q[k]) qs.set(k, q[k]!);
    const res = await fetch(`${API_URL}/courses/browse?${qs}`, FETCH_OPTS);
    if (!res.ok) throw new Error(`Courses API responded ${res.status}`);
    const json = (await res.json()) as {
      data?: ApiCourse[];
      facets?: { categories?: string[]; modes?: string[] };
      pagination?: { count: number; current_page: number; total_pages: number; page_size: number };
    };
    const p = json.pagination;
    return {
      courses: (json.data ?? []).map(mapCourse),
      categories: json.facets?.categories ?? [],
      modes: json.facets?.modes ?? [],
      total: p?.count ?? 0,
      page: p?.current_page ?? page,
      pageCount: p?.total_pages ?? 1,
      pageSize: p?.page_size ?? COURSES_PAGE_SIZE,
    };
  } catch {
    // API unreachable: same filtering over the bundled static catalogue.
    const needle = (q.search ?? "").trim().toLowerCase();
    const list = staticCourses
      .filter((c) => !q.category || c.category === q.category)
      .filter((c) => !q.level || /all levels/i.test(c.level) || c.level.toLowerCase().includes(q.level!.toLowerCase()))
      .filter((c) => !q.mode || c.mode === q.mode)
      .filter((c) => !needle || `${c.title} ${c.summary} ${c.category} ${c.tagline}`.toLowerCase().includes(needle))
      .sort((a, b) => Number(b.featured) - Number(a.featured));
    const pageCount = Math.max(1, Math.ceil(list.length / COURSES_PAGE_SIZE));
    const current = Math.min(page, pageCount);
    return {
      courses: list.slice((current - 1) * COURSES_PAGE_SIZE, current * COURSES_PAGE_SIZE),
      categories: [...new Set(staticCourses.map((c) => c.category))],
      modes: [...new Set(staticCourses.map((c) => c.mode))],
      total: list.length,
      page: current,
      pageCount,
      pageSize: COURSES_PAGE_SIZE,
    };
  }
}

/**
 * null = the API says this course is not public (disabled or unknown slug) -
 * callers should show 404. Static fallback only on network failure.
 */
export async function getSiteCourse(slug: string): Promise<Course | null> {
  try {
    const res = await fetch(`${API_URL}/courses/slug/${encodeURIComponent(slug)}`, FETCH_OPTS);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Course API responded ${res.status}`);
    const json = (await res.json()) as { data?: ApiCourse };
    return json.data ? mapCourse(json.data) : null;
  } catch {
    return getStaticCourse(slug) ?? null;
  }
}
